/**
 * FileActionsMenu Component
 * Context menu for file actions
 */

import { forwardRef } from 'react';
import { Menu, ActionIcon } from '@/components/ui';
import {
  IconDots,
  IconEye,
  IconDownload,
  IconTrash,
  IconFolderSymlink,
  IconCopy,
  IconShare,
} from '@tabler/icons-react';

export interface FileActionsMenuProps {
  /** File ID */
  fileId: string;
  /** Callback to preview file */
  onPreview?: (fileId: string) => void;
  /** Callback to download file */
  onDownload?: (fileId: string) => void;
  /** Callback to delete file */
  onDelete?: (fileId: string) => void;
  /** Callback to move file */
  onMove?: (fileId: string) => void;
  /** Callback to copy file URL */
  onCopyUrl?: (fileId: string) => void;
  /** Callback to share file */
  onShare?: (fileId: string) => void;
  /** Position of the menu */
  position?: 'bottom' | 'left' | 'right' | 'top';
  /** Additional className */
  className?: string;
}

/**
 * Context menu component for file actions
 *
 * @example
 * ```tsx
 * <FileActionsMenu
 *   fileId="file-123"
 *   onPreview={(id) => handlePreview(id)}
 *   onDownload={(id) => handleDownload(id)}
 *   onDelete={(id) => handleDelete(id)}
 *   onMove={(id) => handleMove(id)}
 * />
 * ```
 */
export const FileActionsMenu = forwardRef<HTMLButtonElement, FileActionsMenuProps>(
  (props, ref) => {
    const {
      fileId,
      onPreview,
      onDownload,
      onDelete,
      onMove,
      onCopyUrl,
      onShare,
      position = 'bottom',
      className,
    } = props;

    return (
      <Menu position={position} withinPortal>
        <Menu.Target>
          <ActionIcon
            ref={ref}
            variant="subtle"
            size="sm"
            className={className}
            aria-label="File actions menu"
          >
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          {onPreview && (
            <Menu.Item
              leftSection={<IconEye size={14} />}
              onClick={() => onPreview(fileId)}
            >
              Preview
            </Menu.Item>
          )}

          {onDownload && (
            <Menu.Item
              leftSection={<IconDownload size={14} />}
              onClick={() => onDownload(fileId)}
            >
              Download
            </Menu.Item>
          )}

          {onCopyUrl && (
            <Menu.Item
              leftSection={<IconCopy size={14} />}
              onClick={() => onCopyUrl(fileId)}
            >
              Copy URL
            </Menu.Item>
          )}

          {onShare && (
            <Menu.Item
              leftSection={<IconShare size={14} />}
              onClick={() => onShare(fileId)}
            >
              Share
            </Menu.Item>
          )}

          {onMove && (
            <>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconFolderSymlink size={14} />}
                onClick={() => onMove(fileId)}
              >
                Move to...
              </Menu.Item>
            </>
          )}

          {onDelete && (
            <>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => onDelete(fileId)}
              >
                Delete
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    );
  }
);

FileActionsMenu.displayName = 'FileActionsMenu';

export default FileActionsMenu;
